
export type Options = {
	provider: 'google' | 'apple',
	latitude?: number,
	longitude?: number,
	zoom?: number,
	start?: string,
	end: string,
	endPlaceId?: string,
	query?: string,
	queryPlaceId?: string,
	navigate: boolean,
	travelType: 'drive' | 'walk' | 'public_transport',
	mapType: 'standard' | 'satellite' | 'hybrid' | 'transit'
}

export class Maps {

	public static createLink(options: Options): string {
		// All Options Defined Here
		const ios = false;

		// Assume query is first choice
		const link = {
			google: 'https://www.google.com/maps/search/?api=1&',
			apple: ios ? 'maps://?' : 'http://maps.apple.com/?'
		};

		// Display if lat and longitude is specified
		if (options.latitude && options.longitude) {
			link.google = 'https://www.google.com/maps/@?api=1&map_action=map&';

			// If navigate is navigate with lat and lng params
			if (options.navigate === true) {
				console.warn("Expected 'end' parameter in navigation, defaulting to preview mode.");
				options.navigate = false;
			}
		}

		// Directions if start and end is present
		if (options.end) {
			link.google = 'https://www.google.com/maps/dir/?api=1&';
		}

		const mapQueryParams = this.cleanObject(this.createQueryParameters(options));
		// Escaped commas cause unusual error with Google map
		const url = new URL(link[options.provider]);
		Object.keys(mapQueryParams).forEach(key => {
			url.searchParams.append(key, (mapQueryParams as any)[key]);
		})
		return url.toString();
	}

	private static createQueryParameters(options: Options) {

		switch (options.provider) {
			case 'apple':
				return Maps.createAppleParams(options);
			case 'google':
				return Maps.createGoogleParams(options);
		}

	}

	private static geoCordStringify(latitude: number, longitude: number) {
		[latitude, longitude].map(coord => {
			if (typeof coord !== 'number') {
				throw new Error('Entered a non-number value for geo coordinates.');
			}
		});

		return `${latitude},${longitude}`;
	}

	private static createAppleParams(options: Options) {
		const travelTypeMap = {
			drive: 'd',
			walk: 'w',
			public_transport: 'r'
		};

		const baseTypeMap = {
			satellite: 'k',
			standard: 'm',
			hybrid: 'h',
			transit: 'r',
		}

		let coords;
		if (options.latitude && options.longitude) {
			Maps.geoCordStringify(options.latitude, options.longitude);
		}

		const params = {
			ll: coords,
			z: options.zoom,
			dirflg: (options.travelType) ? travelTypeMap[options.travelType] : null,
			q: options.query,
			saddr: options.start,
			daddr: options.end,
			t: (options.mapType) ? baseTypeMap[options.mapType] : null
		}

		// User performing a query near a location, this requires the z parameter for apple maps
		if (options.query && coords && options.zoom === undefined) {
			params.z = 15;
		}

		return params;
	}

	private static createGoogleParams(options: Options) {
		const travelTypeMap = {
			drive: 'driving',
			walk: 'walking',
			public_transport: 'transit'
		};

		const baseTypeMap = {
			satellite: 'satellite',
			standard: 'roadmap',
			hybrid: 'satellite',
			transit: 'roadmap'
		}

		let coords;
		if (options.latitude && options.longitude) {
			Maps.geoCordStringify(options.latitude, options.longitude);
		}

		const params = {
			origin: options.start,
			destination: options.end,
			destination_place_id: options.endPlaceId,
			travelmode: travelTypeMap[options.travelType],
			zoom: options.zoom,
			basemap: baseTypeMap[options.mapType],
			layer: options.mapType === 'transit' || options.mapType === 'hybrid' ? 'transit' : undefined,
			dir_action: options.navigate ? 'navigate' : undefined,
			center: coords ?? undefined,
			query: coords ? undefined : options.query,
			query_place_id: coords ? undefined : options.queryPlaceId
		};

		return params;
	}

	private static cleanObject(input: any) {
		return Object.keys(input).reduce((acc, key) => {
			const currentValue = input[key];
			return (currentValue) ?
				Object.assign({}, acc, { [key]: currentValue }) : acc;
		}, {});
	}

}